/**
 * Dot-notation nested key utilities for manifest field keys.
 *
 * Manifest fields can use dotted keys like "meta.tune" or "meta.settings.color"
 * which map to nested objects: { meta: { tune: value } }.
 *
 * React-hook-form already handles dot-notation natively in Controller name prop,
 * form.watch(), and form.setValue(). These utilities help the rest of the system
 * (defaults, display options, validation) work with nested structures.
 */

/** Check if a key contains dots (is a nested path) */
export function isNestedKey(key: string): boolean {
  return key.includes('.');
}

/** Get value at a dot-notation path. Returns undefined if any segment is missing. */
export function getNested(obj: Record<string, unknown>, path: string): unknown {
  if (!isNestedKey(path)) return obj[path];

  const parts = path.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

/** Set value at a dot-notation path, creating intermediate objects as needed. */
export function setNested(obj: Record<string, unknown>, path: string, value: unknown): void {
  if (!isNestedKey(path)) {
    obj[path] = value;
    return;
  }

  const parts = path.split('.');
  let current = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    if (!(part in current) || typeof current[part] !== 'object' || current[part] === null) {
      current[part] = {};
    }
    current = current[part] as Record<string, unknown>;
  }
  current[parts[parts.length - 1]] = value;
}

/**
 * Read one entry out of an RHF `formState` sub-tree (`errors`, `touchedFields`, …) by
 * manifest field key.
 *
 * RHF stores DOTTED names nested: `setError('meta.repeatInterval', …)` writes
 * `errors.meta.repeatInterval` and reads it back with RHF's own `get()`. A flat
 * `errors['meta.repeatInterval']` lookup is therefore ALWAYS `undefined` — which silently
 * defeats the `if (!errors[key]) setError(key, …)` dedupe guards in ConfigEngine: the guard
 * never sees the error it just set, so it re-sets it on every render and React dies with
 * "Maximum update depth exceeded". That is what crashed the whole page when Google Slides'
 * `meta.repeatInterval` (Force Sync Interval) was pushed outside its 1–24 range.
 *
 * Flat keys behave identically through this helper, so always prefer it over `[key]`.
 */
function readFormStateEntry(source: unknown, key: string): unknown {
  if (source == null || typeof source !== 'object') return undefined;
  return getNested(source as Record<string, unknown>, key);
}

/** Typed read of one field's error. See {@link readFormStateEntry} for why `errors[key]` breaks. */
export function getFieldError(
  errors: unknown,
  key: string,
): { type?: string; message?: string } | undefined {
  const entry = readFormStateEntry(errors, key);
  return entry != null && typeof entry === 'object'
    ? (entry as { type?: string; message?: string })
    : undefined;
}

/** True when RHF has marked `key` touched. See {@link readFormStateEntry}. */
export function isFieldTouched(touchedFields: unknown, key: string): boolean {
  return readFormStateEntry(touchedFields, key) === true;
}

/**
 * Walk a nested object and return every dotted leaf path that holds a truthy value.
 *
 * Used to translate RHF's `formState.dirtyFields` (shape `{ a: { b: true } }`) into
 * a flat list of dotted paths (`['a.b']`) so we can compare them against the
 * full-path entries in `hiddenCompoundFormKeys`. Without flattening, a top-level
 * dotted key like `options` (containing one dirty leaf) collides with a hidden
 * compound's `options.*` namespace and the legitimate dirty change is wrongly
 * stripped from the Save-button dirty check.
 *
 * Arrays are treated as leaves (RHF marks the whole array dirty, not per index).
 * Empty objects are skipped — they cannot represent a dirty leaf.
 */
export function flattenDottedKeys(obj: unknown, prefix = ''): string[] {
  if (obj == null || typeof obj !== 'object' || Array.isArray(obj)) {
    return obj ? (prefix ? [prefix] : []) : [];
  }
  const out: string[] = [];
  for (const [k, v] of Object.entries(obj)) {
    const path = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      out.push(...flattenDottedKeys(v, path));
    } else if (v) {
      out.push(path);
    }
  }
  return out;
}
